<template>
  <div class="terpene-values">
    <Bar class="terpene-values__chart" :options="chartOptions" :data="chartData" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Bar } from 'vue-chartjs'
import { Chart, registerables } from 'chart.js'
import type { ChartData } from 'chart.js'
import { terpeneCompoundsChartOptions as chartOptions } from '@/components/Charts/chartOptions'
import { lotColors } from '@/components/LotLookup'
import { getLotBarColor } from '@/composables/chartPatterns'
import useLotStore from '@/stores/LotStore'
import type Lot from '@/types/Lot'

Chart.register(...registerables)

const showKey = (keyName: string) => true

const lotStore = useLotStore()

const chartData = computed(() => {
  let datasets = []
  if (lotStore.lots.length !== 1) {
    for (let i = 0; i < lotStore.lots.length; i++) {
      if (lotHasTerpeneChartValues(lotStore.lots[i])) {
        datasets.push({
          label: lotStore.lots[i].lotNumber ?? 'Lot ' + i + 1,
          data: getLotDataSet(lotStore.lots[i]),
          backgroundColor: getLotBarColor(lotStore.lots[i], lotColors[i])
        })
      }
    }
  } else {
    datasets.push({
      label: lotStore.lots[0].lotNumber ?? 'Lot 1',
      data: getLotDataSet(lotStore.lots[0]),
      backgroundColor: getLotBarColor(lotStore.lots[0], lotColors[2])
    })
  }
  let labels = []
  if (showKey('scdMyrcene')) {
    labels.push('Myrcene')
  }
  if (showKey('scdBetaPinene')) {
    labels.push('Beta Pinene')
  }
  if (showKey('scdLimonene')) {
    labels.push('Limonene')
  }
  if (showKey('scdTransCaryophyllene')) {
    labels.push('Trans Caryophyllene')
  }
  if (showKey('scdHumulene')) {
    labels.push('Humulene')
  }

  return {
    labels,
    datasets
  } as ChartData<'bar'>
})

const getLotDataSet = (lot: Lot) => {
  let data = []

  if (showKey('scdMyrcene')) {
    if (lot.brewingValues?.scdMyrcene != null) {
      data.push(lot.brewingValues?.scdMyrcene )
    } else {
      data.push(null)
    }
  }
  if (showKey('scdBetaPinene')) {
    if (lot.brewingValues?.scdBetaPinene != null) {
      data.push(lot.brewingValues?.scdBetaPinene)
    } else {
      data.push(null)
    }
  }
  if (showKey('scdLimonene')) {
    if (lot.brewingValues?.scdLimonene != null) {
      data.push(lot.brewingValues?.scdLimonene )
    } else {
      data.push(null)
    }
  }
  if (showKey('scdTransCaryophyllene')) {
    if (lot.brewingValues?.scdTransCaryophyllene != null) {
      data.push(lot.brewingValues?.scdTransCaryophyllene )
    } else {
      data.push(null)
    }
  }
  if (showKey('scdHumulene')) {
    if (lot.brewingValues?.scdHumulene != null) {
      data.push(lot.brewingValues?.scdHumulene )
    } else {
      data.push(null)
    }
  }
  return data
}

function lotHasTerpeneChartValues(lot: Lot) {
  if (lot.brewingValues) {
    if (
      lot.brewingValues.scdMyrcene ||
      lot.brewingValues.scdBetaPinene ||
      lot.brewingValues.scdLimonene ||
      lot.brewingValues.scdTransCaryophyllene ||
      lot.brewingValues.scdHumulene
    ) {
      return true
    }
  }
  return false
}
</script>

<style scoped lang="scss">
* {
  margin: 0;
  padding: 0;
}

.terpene-values {
  max-width: 100%;
  width: 100%;
  padding: 0;
  margin: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: flex-start;

  &__chart {
    width: 100%;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 0;
    margin: 0;

    p {
      display: none;
    }
  }
}
</style>
